import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProfileStepHeader } from './ProfileStepHeader';
import { useProfileStore } from '../../stores/profileStore';
import { ROLES } from '../../types/auth';

declare global {
  interface Window {
    Supademo?: {
      open: (demoId: string) => void;
    };
  }
}

const DEMO_IDS = {
  [ROLES.RECRUITER]: 'cmn0gzcnv35u1sjdo48zfgx52',
  [ROLES.FREELANCER]: 'cmmmbdpug0o0s9cvjbwzw3aep',
} as const;

const DemoWalkthroughStep: React.FC = () => {
  const { t } = useTranslation('auth');
  const { profile } = useProfileStore();
  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://script.supademo.com/supademo.js';
    script.async = true;
    document.head.appendChild(script);

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'supademo-completed' || event.data?.type === 'supademo-closed') {
        navigate('/dashboard', { replace: true });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      document.head.removeChild(script);
      window.removeEventListener('message', handleMessage);
    };
  }, [navigate]);

  const handleTryTour = () => {
    const demoId =
      profile?.role === ROLES.RECRUITER ? DEMO_IDS[ROLES.RECRUITER] : DEMO_IDS[ROLES.FREELANCER];

    if (window.Supademo) {
      window.Supademo.open(demoId);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="flex flex-col md:flex-row md:min-h-[calc(100vh-4rem)] md:px-8 lg:px-[9.38rem]">
      <div className="flex w-full flex-col pb-8 pt-28 md:w-2/3 md:pr-8">
        <ProfileStepHeader
          firstName={profile?.first_name || ''}
          welcome={t('demoWalkthroughStep.welcome')}
          completeSignup={t('completeSignup')}
          step={t('demoWalkthroughStep.step')}
          completeInfo={t('demoWalkthroughStep.completeInfo')}
          stepDesc={t('demoWalkthroughStep.stepDesc')}
        />
      </div>
      <div className="flex w-full flex-col items-center justify-center py-16 md:w-1/3">
        <div className="w-full max-w-md">
          <button
            className="font-ambit mt-11 w-full rounded-full bg-primary px-4 py-2 text-[1.0625rem] font-[900] not-italic leading-[1.5625rem] text-white"
            onClick={handleTryTour}
          >
            {t('demoWalkthroughStep.tryCta')}
          </button>
          <button
            className="font-ambit mt-4 w-full rounded-full border border-primary bg-transparent px-4 py-2 text-[1.0625rem] font-[900] not-italic leading-[1.5625rem] text-primary"
            onClick={handleSkip}
          >
            {t('demoWalkthroughStep.skipCta')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoWalkthroughStep;
